import { NgClass } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
// services
import { CommentsService } from '@app/core/shared/services/comments.service';
// utils
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { DIALOG_DATA } from '@app/core/shared/utils/dialog/dialog-tokens';

type ReportReason = {
  labelKey: string;
  value: string;
};

type ReportDialogData = {
  commentId?: string;
};

@Component({
  selector: 'pwj-report-dialog',
  standalone: true,
  imports: [TranslateModule, NgClass],
  templateUrl: './report-dialog.component.html',
  styleUrl: './report-dialog.component.scss',
})
export class ReportDialogComponent {
  public readonly reasons: ReportReason[] = [
    {
      labelKey: 'reportComment.reasons.spamOrMisleading',
      value: 'spam_or_misleading',
    },
    {
      labelKey: 'reportComment.reasons.harassmentOrHateSpeech',
      value: 'harassment_or_hate_speech',
    },
    {
      labelKey: 'reportComment.reasons.inappropriateContent',
      value: 'inappropriate_content',
    },
    {
      labelKey: 'reportComment.reasons.intellectualPropertyViolation',
      value: 'intellectual_property_violation',
    },
    {
      labelKey: 'reportComment.reasons.other',
      value: 'other',
    },
  ];
  public readonly selectedReason = signal<string | null>(null);
  public readonly submitting = signal<boolean>(false);
  public readonly isMobileView = signal<boolean>(false);

  commentId = input<string | null>(null);
  closed = output<void>();

  // dependencies
  private readonly _commentsService = inject(CommentsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _dialogRef = inject(DialogRef, { optional: true });
  private readonly _dialogData = inject<ReportDialogData | null>(DIALOG_DATA, {
    optional: true,
  });

  constructor() {
    this._setMobileState();
  }

  @HostListener('window:resize')
  onResize() {
    this._setMobileState();
  }

  public close() {
    if (this._dialogRef) {
      this._dialogRef.close();
      return;
    }

    this.closed.emit();
  }

  public selectReason(value: string) {
    this.selectedReason.set(value);
  }

  public submit() {
    const reason = this.selectedReason();
    const commentId = this._resolveCommentId();

    if (!reason || !commentId || this.submitting()) {
      return;
    }

    const reasonLabel =
      this.reasons.find(item => item.value === reason)?.labelKey || reason;
    const reportText = this._translateService.instant(reasonLabel);

    this.submitting.set(true);
    this._commentsService
      .reportComment(commentId, reportText)
      .pipe(finalize(() => this.submitting.set(false)))
      .subscribe(success => {
        if (success) {
          this.close();
        }
      });
  }

  private _resolveCommentId(): string | null {
    return this.commentId() || this._dialogData?.commentId || null;
  }

  private _setMobileState() {
    this.isMobileView.set(window.matchMedia('(max-width: 47.99em)').matches);
  }
}
